// Playground project I/O. The kid's game files are REAL — they live in the
// backend's storage (S3, Sydney) + Neon metadata, and are read through the
// shared `api` client (JWT + refresh), NOT fetched from S3 by the browser. This
// mirrors the code studio: a game is a backend project whose VFS the server
// seeds from a Phaser template at create time (decision D-CODE1) and serves at
// `GET /projects/:id/code/files`. The kid surface never holds S3 credentials.
//
// `resolveProjectFiles` is the single entry the UI calls: it loads the real
// files when a project is available, and falls back to the local starter
// scaffold when one isn't (a project-less session — e.g. the create/landing flow
// before a project exists, and the game backend isn't built yet) — so the
// playground always opens with files.

import { api, apiBlob, BASE_URL } from '@/lib/api';
import { readVfs } from '../../code/codeApi';
import type { VfsFile } from '../../code/codeApi';
import type { StudioKind } from '../studioKind';
import { generateScaffold } from './starterProject';

/** Project kind for games (a Phaser-templated backend project). */
export const GAME_PROJECT_KIND = 'game' as const;

// ── Try-demo seam (try-demo-mode-prd D-DEMO-02/03) ────────────────────────────
// The public `/try/playground` demo bundles its starter VFS client-side;
// `resolveProjectFiles` serves it instead of the backend / local scaffold.
// Installed/cleared by `src/pages/try/demoAdapters.ts`; null (off) everywhere else.
let demoProjectFiles: (() => VfsFile[]) | null = null;
export function setDemoProjectFiles(provider: (() => VfsFile[]) | null): void {
  demoProjectFiles = provider;
}

/**
 * The starter templates the backend seeds at create time (PRD §3 OQ-2 / §12 +
 * learn-game-studio-3d-prd.md D-3D-06c). Phaser (2D): `phaser_pong`,
 * `phaser_catcher`, `phaser_blank`. three.js (3D): `three_spin`, `three_collect`,
 * `three_blank`. The hub's picture chips map onto these.
 */
export type GameTemplateId =
  | 'phaser_pong'
  | 'phaser_catcher'
  | 'phaser_blank'
  | 'three_spin'
  | 'three_collect'
  | 'three_blank';

/** Project kind for websites (creative-code-studio-website-prd) — the Website
 *  Studio runs in the SAME playground with a site runtime instead of a game. */
export const WEBSITE_PROJECT_KIND = 'website' as const;

/** The website starter templates the backend seeds (`website_blank` is the
 *  default when none is passed; `website_pet_shop` is the worked example). */
export type WebsiteTemplateId = 'website_blank' | 'website_pet_shop';

/** Project kind for chatbots (creative-code-studio-chatbot-prd D-CB-01): the
 *  Chatbot Studio runs in the SAME playground on the website runtime plus the
 *  `window.ai` chat bridge to the kid's bot brain. */
export const CHATBOT_PROJECT_KIND = 'chatbot' as const;

/** The chatbot starter templates the backend seeds (`chatbot_blank` is the
 *  default; `chatbot_story_buddy` is the worked persona, D-CB-16). */
export type ChatbotTemplateId = 'chatbot_blank' | 'chatbot_story_buddy';

/**
 * Create a REAL `kind='game'` backend project (PRD J1 / §4.2). The backend seeds
 * the starter template into the S3-backed VFS and returns the project id; the
 * studio then opens on the real files via `readVfs`.
 *
 * `template` is OPTIONAL: when omitted (the free-prompt landing path), the backend
 * INFERS the engine (2D Phaser vs 3D three.js) from the prompt/title and seeds the
 * matching blank starter (learn-game-studio-3d-prd.md D-3D-07). Pass `template`
 * only when the kid explicitly picked a starter chip. Do NOT hardcode a Phaser
 * template here — that would force every game (incl. "make a 3D …") into 2D.
 */
export async function createGameProject(args: {
  kidId: string | null;
  familyId: string | null;
  /** The kid's prompt / chosen game name — the backend infers the engine from it. */
  title: string;
  /** `game` (default), `website` (Website Studio) or `chatbot` (Chatbot
   *  Studio) — same playground, different runtime + templates
   *  (creative-code-studio-website-prd / creative-code-studio-chatbot-prd). */
  kind?: StudioKind;
  /** Only set when a specific starter was chosen; omit to let the backend pick
   *  (games: engine inferred from the title; websites: `website_blank`;
   *  chatbots: `chatbot_blank`). */
  template?: GameTemplateId | WebsiteTemplateId | ChatbotTemplateId;
  /**
   * Generic prompt-first landing (D-WEB-11): ask the SERVER to route
   * game-vs-website from the title with one classify-model call (keyword
   * fallback, never fails the create). The response's `kind` is the decision —
   * adopt it. Explicit studio choices omit this.
   */
  inferKind?: boolean;
  /**
   * Creative Code Challenge entrant onboarding (entrant-onboarding-prd §8.3):
   * the edition slug this project is being built for. The backend resolves it to
   * `Project.challenge_edition_id` and stores it, which is the ONLY thing that
   * survives — the studio `replaceState`s straight after creation and discards
   * every query param, exactly as it does for `?class=`.
   */
  challengeSlug?: string;
}): Promise<{ id: string; kind?: StudioKind }> {
  return api<{ id: string; kind?: StudioKind }>(`/projects`, {
    method: 'POST',
    body: {
      title: args.title,
      product_line: 'line_b_coding',
      kind: args.kind ?? GAME_PROJECT_KIND,
      ...(args.inferKind ? { infer_kind: true } : {}),
      ...(args.challengeSlug ? { challenge_slug: args.challengeSlug } : {}),
      ...(args.template ? { template: args.template } : {}),
      ...(args.kidId ? { kid_id: args.kidId } : {}),
      ...(args.familyId ? { family_id: args.familyId } : {}),
    },
  });
}

/**
 * Attach a just-created game to the class it was launched from. This mirrors the
 * My Works placement contract instead of overloading project creation, so the
 * game still gets created only after the kid submits the first prompt.
 */
export async function placeGameProjectForClass(args: { projectId: string; classId: string }): Promise<void> {
  await api<void>(`/projects/${args.projectId}/placement`, {
    method: 'PATCH',
    body: { action: 'use_for_class', class_id: args.classId },
  });
}

/**
 * Create a teacher-owned PREP game project (teacher-prep-projects). This is the
 * teacher counterpart of {@link createGameProject}: it hits the teacher-scoped
 * `POST /classes/:id/prep-projects` (roles teacher/admin, only a delivering teacher),
 * which creates a real `is_teacher_prep` game with `kid_id`/`family_id` null and a
 * real VFS — so the teacher gets the SAME prompt-first landing → generate flow a kid
 * gets, at 0 Stars. `engine` is INFERRED server-side from the title (2D/3D), exactly
 * like the kid path — do NOT pass a template here.
 */
export async function createPrepGameProject(args: {
  classId: string;
  /** The teacher's prompt / game name — the backend infers the engine from it. */
  title: string;
}): Promise<{ id: string }> {
  return api<{ id: string }>(`/classes/${args.classId}/prep-projects`, {
    method: 'POST',
    body: { title: args.title, kind: GAME_PROJECT_KIND },
  });
}

/**
 * Create a game in the teacher's own PLAYGROUND (teacher-playground-stars-prd.md).
 *
 * The sibling of {@link createPrepGameProject}, with the one difference that
 * matters: it carries NO class, because the Playground is the teacher's own space.
 * The backend refuses it unless the teacher holds an active Star grant, and every
 * AI turn on the result spends those Stars — unlike class prep, which is free.
 */
export async function createPlaygroundGameProject(args: {
  /** The teacher's prompt / game name — the backend infers the engine from it. */
  title: string;
}): Promise<{ id: string }> {
  return api<{ id: string }>('/teacher/playground-projects', {
    method: 'POST',
    body: { title: args.title, kind: GAME_PROJECT_KIND },
  });
}

/**
 * Transcribe a voice idea to text for the prompt box (UDL / OD-6 voice input).
 * STT runs SERVER-SIDE via `platform-backend /llm/transcribe` — the kid surface
 * NEVER calls an LLM/STT provider directly (airbotix-app CLAUDE.md #5). The mic
 * captures audio in the browser and posts it (base64 data URL, like the asset
 * pipeline) through the shared `api` client; only the transcript text comes back.
 */
export async function transcribeVoice(args: { audioDataUrl: string }): Promise<{ text: string }> {
  return api<{ text: string }>(`/llm/transcribe`, {
    method: 'POST',
    body: { audio: args.audioDataUrl },
  });
}

/**
 * Load a game project's files from the backend (S3-backed, Sydney) via the
 * shared `api` client. Thin wrapper over the code studio's `readVfs` — the same
 * `GET /projects/:id/code/files` endpoint serves every project kind.
 */
export async function loadGameFiles(projectId: string): Promise<VfsFile[]> {
  return readVfs(projectId);
}

// ── Class shared asset library (class-shared-assets-prd) ─────────────────────
// A teacher prepares media assets on a class; a kid whose game project belongs
// to that class can browse them in the Asset Viewer's "Class" tab and pull a
// copy into their own VFS. The backend gates access: it returns `[]` unless the
// project is `class_work`/`class` for a class the kid is enrolled in — so the
// frontend simply hides the Class tab when the list is empty (server is the
// source of truth for authz, airbotix-app CLAUDE.md #4).

/**
 * A class-shared asset the kid can browse (backend `ClassAssetView`). The
 * endpoint now MERGES two sources into one flat list (D-CSA-3): the bound course
 * pack's admin-curated DEFAULTS (`source:'course'`, no `class_id`) come first,
 * then the class's own teacher assets (`source:'class'`). The kid consumes both
 * identically — referenced directly by `assets/class/<name>` (Model A, no copy);
 * `source` only labels the origin in the UI.
 */
export interface ClassAssetView {
  id: string;
  /** Absent for course-pack defaults (they have no owning class). */
  class_id?: string;
  name: string;
  /**
   * Full parity with the playground's own importable set (D-3D-09 + data assets):
   * media plus 3D `model` (glb) and non-executable `other` data (game data, fonts,
   * shaders). A sprite strip arrives as an `image` item PLUS a sibling `other`-kind
   * item named `<image-basename>.anim.json` (the sidecar) in the same list — the UI
   * derives "sprite" from that pairing, never from a distinct backend kind.
   */
  kind: 'image' | 'audio' | 'video' | 'model' | 'other';
  mime_type: string;
  size_bytes: number;
  created_at: string;
  /** Legacy short-lived signed GET URL returned by the list contract. Creative
   *  Code Studio does not consume it: previews and runtime resolution fetch the
   *  authenticated project-scoped bytes endpoint by asset id. */
  download_url: string;
  /** Permanent public media path. Anyone with this URL can read the asset. */
  public_url?: string;
  /** Where this asset comes from — course-pack default vs class (teacher) asset. */
  source: 'class' | 'course';
}

/**
 * List the shared assets visible to this project (class-shared-assets-prd).
 * `GET /projects/:id/class-assets` MERGES the bound course pack's defaults
 * (`source:'course'`, first) and the class's teacher assets (`source:'class'`)
 * into one flat list (D-CSA-3). It returns `[]` unless the project is class work
 * for a class the kid is enrolled in — the backend gate is the source of truth,
 * so the caller hides the Class tab when the list is empty.
 */
export async function listClassAssets(projectId: string): Promise<ClassAssetView[]> {
  return api<ClassAssetView[]>(`/projects/${projectId}/class-assets`);
}

/** Read a Blob's bytes as a `data:` URL — the uniform VFS shape for binary media. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read asset bytes'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetch a referenced class/course asset's bytes and convert them to a `data:` URL —
 * the uniform VFS shape for a binary asset (the same representation imports use).
 * The studio inlines this into the sandboxed game (class-shared-assets-prd, Model A)
 * and, for a 3D asset, into the model preview.
 *
 * The bytes come SAME-ORIGIN from the backend proxy
 * (`GET /projects/:projectId/class-assets/:assetId/bytes`), NOT from the signed S3
 * `download_url`: a browser `fetch()` of that cross-origin URL requires media-bucket
 * CORS that isn't set, so it fails (while the `<img>` preview, which ignores CORS,
 * still works) and the game would be left with a bare `assets/class/<name>` path it
 * can't load in the opaque-origin frame. The proxy is on the app origin (same CORS
 * posture as every API call) and keeps the signed S3 URL server-side.
 */
export async function fetchClassAssetDataUrl(
  projectId: string,
  assetId: string,
  publicUrl?: string,
): Promise<string> {
  // Runtime and model parsing must prefer the authenticated project route. The
  // permanent-public-link rollout accidentally made `public_url` the only read
  // whenever it was present. A public-route or rollout failure then removed the
  // virtual asset and left the opaque runner to fetch the bare relative path.
  // Keep the authorised route as the source of truth and retain the public route
  // only as a compatibility fallback for a temporarily unavailable private read.
  let blob: Blob;
  try {
    blob = await apiBlob(`/projects/${projectId}/class-assets/${assetId}/bytes`);
  } catch (privateError) {
    if (!publicUrl) throw privateError;
    const response = await fetch(`${BASE_URL}${publicUrl}`);
    if (!response.ok) throw privateError;
    blob = await response.blob();
  }
  return blobToDataUrl(blob);
}

// ── Website project database (creative-code-studio-website-prd D-WEB-15) ─────
// Each website project owns ONE server-side SQLite database on platform-backend,
// torn down with the project. The sandboxed site frame stays token-free: its
// `db.query(...)` calls travel over postMessage to SiteFrame, which calls these
// endpoints with the kid's session. The Database window introspects the same db
// over REST (tables/rows) and owns the ONLY reset path (rebuild from the
// project's top-level `data/*.json` seed files).

/** A bindable SQL parameter (the backend binds them — never string-splice). */
export type SiteDbParam = string | number | boolean | null;

/** `POST /projects/:id/db/query` result — one statement, rows capped server-side. */
export interface SiteDbQueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  changes: number | null;
  last_insert_rowid: string | null;
}

/** Run ONE SQL statement (params bound server-side) on the project's db. SQL
 *  errors surface as a 400 `DB_QUERY_ERROR` ApiError whose message is
 *  kid-readable — callers show it verbatim. */
export async function querySiteDb(
  projectId: string,
  sql: string,
  params: SiteDbParam[] = [],
): Promise<SiteDbQueryResult> {
  return api<SiteDbQueryResult>(`/projects/${projectId}/db/query`, {
    method: 'POST',
    body: { sql, params },
  });
}

export interface SiteDbColumn {
  name: string;
  type: string;
}

export interface SiteDbTableInfo {
  name: string;
  row_count: number;
  columns: SiteDbColumn[];
}

/** `GET /projects/:id/db/tables` — the schema snapshot the Database window renders. */
export interface SiteDbTables {
  tables: SiteDbTableInfo[];
  size_bytes: number;
}

export async function listSiteDbTables(projectId: string): Promise<SiteDbTables> {
  return api<SiteDbTables>(`/projects/${projectId}/db/tables`);
}

/** The key carrying each introspected row's stable SQLite rowid (D-WEB-16) —
 *  the Database window's edit/delete WHERE key. Present on every row when the
 *  table reports `has_rowid: true`; NEVER a display column. */
export const SITE_DB_ROWID_KEY = '__rowid__';

/** Page through one table's rows (backend default 100, max 500 per page).
 *  `has_rowid` reports whether the table has a usable rowid (D-WEB-16) — when
 *  true each row carries its rowid (string) under {@link SITE_DB_ROWID_KEY}. */
export async function listSiteDbRows(
  projectId: string,
  table: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ rows: Record<string, unknown>[]; total: number; has_rowid: boolean }> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return api<{ rows: Record<string, unknown>[]; total: number; has_rowid: boolean }>(
    `/projects/${projectId}/db/tables/${encodeURIComponent(table)}/rows${qs ? `?${qs}` : ''}`,
  );
}

/** Rebuild EVERY table from the project's `data/*.json` seeds (wipes changes) —
 *  the explicit Reset database affordance, the only reset path. */
export async function resetSiteDb(projectId: string): Promise<SiteDbTables> {
  return api<SiteDbTables>(`/projects/${projectId}/db/reset`, { method: 'POST' });
}

// ── External data sources (creative-code-studio-website-prd D-WEB-19/23) ─────
// External data the site can use WITHOUT any sandbox egress: the in-frame
// `sources.get(name, params)` (admin-curated catalog) and `sources.fetch(url)`
// (ANY public https JSON API, D-WEB-23) ride postMessage to SiteFrame, which
// calls these endpoints with the kid's session, and the BACKEND fetches the
// real provider (validated, cached, size/time capped; open fetches pass the
// server's SSRF fence + the admin domain blocklist). The Database window's
// "Data sources" group renders the curated catalog.


/** A data-source parameter value — scalars only, per the backend contract. */
export type SiteSourceParam = string | number | boolean;

/** `POST /projects/:id/sources/:name` — fetch one source through the server
 *  proxy. Failures arrive as 400 `SOURCE_UNKNOWN` / `SOURCE_PARAMS` /
 *  `SOURCE_UPSTREAM` or 429 `SOURCE_BUSY` (server-side per-project cap)
 *  ApiErrors whose messages are kid-readable — callers surface them
 *  verbatim. */
export async function fetchProjectSource(
  projectId: string,
  name: string,
  params: Record<string, SiteSourceParam>,
): Promise<{ data: unknown; cached: boolean }> {
  return api<{ data: unknown; cached: boolean }>(
    `/projects/${projectId}/sources/${encodeURIComponent(name)}`,
    { method: 'POST', body: { params } },
  );
}

/** `POST /projects/:id/sources/fetch` — the OPEN fetch door (D-WEB-23): any
 *  public https JSON API through the server proxy (SSRF fence + admin domain
 *  blocklist enforced server-side). Failures arrive as 400 `SOURCE_URL` /
 *  `SOURCE_BLOCKED` / `SOURCE_UPSTREAM` or 429 `SOURCE_BUSY` ApiErrors whose
 *  messages are kid-readable — callers surface them verbatim. */
export async function fetchProjectSourceUrl(
  projectId: string,
  url: string,
): Promise<{ data: unknown; cached: boolean }> {
  return api<{ data: unknown; cached: boolean }>(`/projects/${projectId}/sources/fetch`, {
    method: 'POST',
    body: { url },
  });
}

export interface ResolveFilesOptions {
  /** When present, load the real project files from the backend (S3-backed). */
  projectId?: string;
  /** Used only by the local fallback scaffold (stamped into its entry file). */
  prompt: string;
  /** The kid's game name (PRD J1) — preferred over the prompt to title the scaffold. */
  name?: string;
}

/**
 * Resolve the files to open in the workspace.
 *
 * For a REAL project (`projectId` present) the backend VFS is the SOURCE OF
 * TRUTH — we load it and nothing else. There is deliberately **no scaffold
 * fallback**: if the files can't be loaded (network / auth / backend / a project
 * with no files) we THROW so the caller can show an error and send the kid back
 * to project creation, rather than silently opening a fake starter game.
 *
 * Only when there is NO project — a project-less session (e.g. the create/landing
 * flow before a project exists), not a saved project — do we return the local
 * starter scaffold so the editor never opens empty.
 */
export async function resolveProjectFiles(opts: ResolveFilesOptions): Promise<VfsFile[]> {
  const { projectId, prompt, name } = opts;
  // Demo mode: the bundled demo starter, no network (try-demo-mode-prd D-DEMO-02).
  if (demoProjectFiles) return demoProjectFiles();
  if (projectId) {
    const files = await loadGameFiles(projectId);
    if (files.length === 0) {
      throw new Error(`Project ${projectId} loaded no files`);
    }
    return files;
  }
  // No project (a project-less session): the local scaffold, titled from the prompt/name.
  return generateScaffold(name?.trim() || prompt);
}

// ── Chatbot Studio runtime replies (creative-code-studio-chatbot-prd §3.5 / §7) ──
// The sandboxed chat page's `ai.chat(text)` travels over postMessage to the
// studio SiteFrame, which calls these with the kid's session (the frame stays
// token-free). Every DELIVERED reply charges Stars through the backend's paid
// text pipeline; the server reads the bot's brain (`prompt.md` / `bot.json`)
// from the live VFS — the browser never sends a system prompt (D-CB-04).

/** `POST /projects/:id/chatbot/reply` result (the public variant adds `session`). */
export interface ChatbotReplyResult {
  reply: string;
  stars_charged: number;
  balance_after?: number;
  budget_remaining?: number;
  session?: string;
}

/** The Stars one runtime reply costs right now (`GET /projects/:id/chatbot/price`). */
export async function getChatbotPrice(
  projectId: string,
): Promise<{ stars: number; model: string; source: string }> {
  return api<{ stars: number; model: string; source: string }>(`/projects/${projectId}/chatbot/price`);
}

/**
 * ONE runtime reply from the kid's bot. `history` is the newest few transcript
 * lines (the server trims again, D-CB-08); `idempotencyKey` makes a retried
 * send replay the stored reply instead of charging twice (D-CB-10);
 * `expectedStars` is the price the page showed — a mismatch is a 409 the
 * caller surfaces, never a silent different charge.
 */
export async function sendChatbotReply(
  projectId: string,
  args: {
    text: string;
    history: Array<{ role: 'user' | 'bot'; text: string }>;
    idempotencyKey?: string;
    expectedStars?: number;
    piiWarnAcknowledged?: boolean;
  },
): Promise<ChatbotReplyResult> {
  return api<ChatbotReplyResult>(`/projects/${projectId}/chatbot/reply`, {
    method: 'POST',
    body: {
      text: args.text,
      history: args.history,
      idempotency_key: args.idempotencyKey ?? crypto.randomUUID(),
      ...(args.expectedStars !== undefined ? { expected_stars: args.expectedStars } : {}),
      ...(args.piiWarnAcknowledged ? { pii_warn_acknowledged: true } : {}),
    },
  });
}
